import * as THREE from 'three';
import { Vehicle } from './Vehicle';
import { Tree } from './environment/Tree';
import { Rock } from './environment/Rock';
import { Cloud } from './environment/Cloud';
import { Road } from './environment/Road';
import { Vegetation } from './environment/Vegetation';
import { Water } from 'three/examples/jsm/objects/Water.js';

interface TreeData {
  mesh: THREE.Group;
  initialZ: number;
}

export class VehicleGameEngine {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private road!: THREE.Mesh;
  private obstacles: THREE.Object3D[];
  private vehicle: Vehicle;
  private controls: { [key: string]: boolean } = {};
  private treesData: TreeData[] = [];
  private clouds: THREE.Object3D[] = [];
  private terrain!: THREE.Mesh;
  private initialTerrainZ: number = 0;
  
  // Thêm thuộc tính âm thanh
  private engineSound: HTMLAudioElement;
  private farmSound: HTMLAudioElement;
  private hornSound: HTMLAudioElement;
  private isEnginePlaying: boolean = false;

  constructor(vehicle: Vehicle) {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.obstacles = [];
    this.vehicle = vehicle;

    // Khởi tạo âm thanh
    this.engineSound = new Audio('/sounds/engine.mp3');
    this.engineSound.loop = true;
    this.engineSound.volume = 0.2;
    this.farmSound = new Audio('/sounds/farm-noise.mp3');
    this.farmSound.loop = true;
    this.farmSound.volume = 0.4; // Giảm âm lượng xuống thấp hơn để làm âm thanh nền
    this.farmSound.autoplay = true; // Tự động phát
    this.hornSound = new Audio('/sounds/horn.mp3');
    
    // Đảm bảo âm thanh môi trường luôn phát
    this.farmSound.addEventListener('canplaythrough', () => {
      this.farmSound.play().catch(e => {
        console.log("Không thể tự động phát âm thanh môi trường:", e);
        // Thêm sự kiện click để phát âm thanh (giải quyết vấn đề autoplay policy của trình duyệt)
        document.addEventListener('click', () => {
          if (this.farmSound.paused) {
            this.farmSound.play().catch(e => console.log("Vẫn không thể phát âm thanh:", e));
          }
        }, { once: true });
      });
    });
    this.hornSound = new Audio('/sounds/horn.mp3');
    
    // Phát âm thanh nông trại ngay khi khởi tạo
    this.farmSound.play().catch(e => console.log("Không thể phát âm thanh nông trại:", e));

    const vehicleMesh = this.vehicle.getMesh();
    vehicleMesh.position.set(0, 0.3, -495);

    this.initializeBasicScene();
    this.setupControls();
  }

  private setupControls(): void {
    window.addEventListener('keydown', (event) => {
      this.controls[event.key] = true;
      
      // Phát âm thanh còi khi nhấn Space
      if (event.key === ' ' || event.code === 'Space') {
        this.hornSound.currentTime = 0; // Reset âm thanh nếu đang phát
        this.hornSound.play().catch(e => console.log("Không thể phát âm thanh còi:", e));
      }
    });

    window.addEventListener('keyup', (event) => {
      this.controls[event.key] = false;
      
      // Dừng âm thanh động cơ khi nhả phím di chuyển
      if (event.key === 'ArrowUp') {
        this.stopEngineSound();
      }
    });
  }

  // Thêm phương thức phát âm thanh động cơ
  private playEngineSound(): void {
    if (!this.isEnginePlaying) {
      this.engineSound.currentTime = 0;
      this.engineSound.play().catch(e => console.log("Không thể phát âm thanh động cơ:", e));
      this.isEnginePlaying = true;
    }
  }

  // Thêm phương thức dừng âm thanh động cơ
  private stopEngineSound(): void {
    if (this.isEnginePlaying) {
      this.engineSound.pause();
      this.isEnginePlaying = false;
    }
  }

  private initializeBasicScene(): void {
    // Đổi màu nền thành màu xanh sáng đơn giản, không dùng gradient
    this.scene.background = new THREE.Color(0x87CEEB); // Sky blue - màu xanh trời sáng
    
    // Bỏ phần tạo gradient và shader phức tạp
    
    // Thêm mặt trời - khối cầu màu vàng với vị trí thấp hơn nữa
    const sunGeometry = new THREE.SphereGeometry(15, 32, 32);
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff00, // Màu vàng
      transparent: true,
      opacity: 0.8
    });
    const sun = new THREE.Mesh(sunGeometry, sunMaterial);
    sun.position.set(100, 5, -200); // Vị trí thấp hơn nhiều (y từ 20 xuống 5)
    sun.name = 'sun';
    this.scene.add(sun);
    
    // Cập nhật màu sương mù để phù hợp với màu trời
    this.scene.fog = new THREE.FogExp2(0x87CEEB, 0.00008);

    const roadWidth = 24;
    const sidewalkWidth = 2;
    const sidewalkHeight = 0.2;
    const roadLength = 2000;

    // Removed Sky object creation and configuration
    
    this.road = Road.createMainRoad(roadWidth, roadLength);
    this.road.rotation.x = -Math.PI / 2;
    this.road.position.y = -0.29;
    this.scene.add(this.road);

    const leftSidewalk = Road.createSidewalk(sidewalkWidth, sidewalkHeight, roadLength);
    leftSidewalk.position.set(-(roadWidth / 2 + sidewalkWidth / 2), -0.25, 0);
    leftSidewalk.name = 'sidewalk';
    this.scene.add(leftSidewalk);

    const rightSidewalk = Road.createSidewalk(sidewalkWidth, sidewalkHeight, roadLength);
    rightSidewalk.position.set(roadWidth / 2 + sidewalkWidth / 2, -0.25, 0);
    rightSidewalk.name = 'sidewalk';
    this.scene.add(rightSidewalk);

    this.createEnvironment();

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 20, 10);
    this.scene.add(directionalLight);

    this.scene.add(this.vehicle.getMesh());

    const waterGeometry = new THREE.PlaneGeometry(100, 100);
    const water = new Water(waterGeometry, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals: new THREE.TextureLoader().load('/textures/water.jpg', function (texture) {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      }),
      sunDirection: new THREE.Vector3(),
      sunColor: 0xffffff,
      waterColor: 0x001e0f,
      distortionScale: 3.7,
      fog: this.scene.fog !== undefined
    });
    water.rotation.x = -Math.PI / 2;
    water.position.set(-150, -0.28, -100);
    this.scene.add(water);

    this.addTerrain();
  }

  private addTerrain(): void {
    const terrainGeometry = new THREE.PlaneGeometry(1000, 1000, 256, 256);

    const terrainMaterial = new THREE.MeshStandardMaterial({
      color: 0x66BB6A, // Brighter and greener color
      wireframe: false,
    });

    this.terrain = new THREE.Mesh(terrainGeometry, terrainMaterial);
    this.terrain.rotation.x = -Math.PI / 2;
    this.terrain.position.y = -0.8;
    this.initialTerrainZ = this.terrain.position.z;
    this.scene.add(this.terrain);
  }

  private createEnvironment(): void {
    Vegetation.createGrassAndFlowers(this.scene);

    for (let i = 0; i < 20; i++) {
      const cloud = Cloud.create();
      cloud.position.set(
        Math.random() * 1000 - 500,
        Math.random() * 20 + 50,
        Math.random() * 1000 - 500
      );
      this.scene.add(cloud);
      this.clouds.push(cloud);
    }

    const treeGroup = new THREE.Group();
    const treesData: TreeData[] = [];

    for (let i = 0; i < 100; i++) {
      const z = Math.random() * 1800 - 900;

      const leftTree = Tree.create();
      const leftX = -20 - Math.random() * 30;
      leftTree.position.set(leftX, 0, z);
      treeGroup.add(leftTree);
      treesData.push({ mesh: leftTree, initialZ: z });

      const rightTree = Tree.create();
      const rightX = 20 + Math.random() * 30;
      rightTree.position.set(rightX, 0, z);
      treeGroup.add(rightTree);
      treesData.push({ mesh: rightTree, initialZ: z });

      if (i % 2 === 0) {
        const rock = Rock.create();
        const rockX = (Math.random() > 0.5 ? 1 : -1) * (15 + Math.random() * 35);
        const rockZ = Math.random() * 1800 - 900;

        rock.position.set(rockX, 0, rockZ);
        rock.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );

        this.scene.add(rock);
      }
    }

    this.scene.add(treeGroup);
    this.treesData = treesData;
  }

  public update(): void {
    const vehicleMesh = this.vehicle.getMesh();
    const currentPosition = vehicleMesh.position.z;
    
    // Di chuyển mặt trời theo vị trí xe với độ cao thấp hơn nữa
    const sun = this.scene.getObjectByName('sun');
    if (sun) {
      // Giữ mặt trời ở vị trí tương đối so với xe
      sun.position.z = currentPosition - 200;
      
      // Tạo hiệu ứng mặt trời lặn dần khi xe di chuyển - thấp hơn nhiều
      const sunHeight = 5 + Math.sin(currentPosition * 0.001) * 3;
      sun.position.y = sunHeight;
    }

    if (Math.abs(currentPosition) > 1000) {
      this.road.position.z = currentPosition;
      this.scene.children.forEach(child => {
        if (child.name === 'sidewalk') {
          child.position.z = currentPosition;
        }
      });
    }

    this.treesData.forEach(treeData => {
      if (currentPosition - treeData.mesh.position.z > 1000) {
        treeData.mesh.position.z += 2000;
      } else if (treeData.mesh.position.z - currentPosition > 1000) {
        treeData.mesh.position.z -= 2000;
      }
    });

    this.clouds.forEach(cloud => {
      cloud.position.x += 0.1;
      if (cloud.position.x > 500) {
        cloud.position.x = -500;
      }
    });

    if (currentPosition - this.terrain.position.z > 500) {
      this.terrain.position.z += 1000;
    } else if (this.terrain.position.z - currentPosition > 500) {
      this.terrain.position.z -= 1000;
    }

    if (this.controls['ArrowUp']) {
      this.vehicle.accelerate();
      this.playEngineSound(); // Phát âm thanh động cơ khi di chuyển
    } else if (!this.controls['ArrowUp'] && this.isEnginePlaying) {
      this.stopEngineSound(); // Dừng âm thanh khi không di chuyển
    }
    
    if (this.controls['ArrowDown']) {
      this.vehicle.brake();
    }
    if (this.controls['ArrowLeft']) {
      this.vehicle.turnLeft();
    }
    if (this.controls['ArrowRight']) {
      this.vehicle.turnRight();
    }

    this.vehicle.update();

    // Modified camera position for better road visibility
    const idealOffset = new THREE.Vector3(0, 8, -15); // Higher Y and further back Z
    const idealLookat = new THREE.Vector3(0, 0, 10); // Looking further ahead

    const vehiclePosition = this.vehicle.getMesh().position;

    const targetPosition = new THREE.Vector3();
    targetPosition.copy(vehiclePosition).add(idealOffset);

    const lookAtPosition = new THREE.Vector3();
    lookAtPosition.copy(vehiclePosition).add(idealLookat);

    this.camera.position.lerp(targetPosition, 0.1);
    this.camera.lookAt(lookAtPosition);
  }

  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  public getScene(): THREE.Scene {
    return this.scene;
  }

  public handleMobileControl(control: string, isPressed: boolean): void {
    switch (control) {
      case 'up':
        this.controls['ArrowUp'] = isPressed;
        break;
      case 'down':
        this.controls['ArrowDown'] = isPressed;
        break;
      case 'left':
        this.controls['ArrowLeft'] = isPressed;
        break;
      case 'right':
        this.controls['ArrowRight'] = isPressed;
        break;
    }
  }
}
